import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';

import { showResult } from '../utils';
import { getUnregisteredConversationNotesKey } from './useUnregisteredConversationNotes';

interface Props {
  onSuccess: () => void;
}

const updateUnregisteredConversationNotesFn = async ({
  conversationId,
  note,
  noteGuid,
}: {
  conversationId: number;
  note: string;
  noteGuid: string;
}) => {
  const API_UNREGISTERED_CONVERSATION_NOTES = `core-2/conversations/${conversationId}/notes`;
  const { data, error } = await dataService.updateOnly(
    API_UNREGISTERED_CONVERSATION_NOTES,
    {
      note,
      noteGuid,
    },
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const useUpdateUnregisteredConversationNotes = ({
  onSuccess,
}: Props) => {
  const queryClient = useQueryClient();
  const { mutate, isLoading, ...other } = useMutation({
    mutationFn: updateUnregisteredConversationNotesFn,
    onSuccess(data, variables) {
      const unregisteredConversationNotesKey =
        getUnregisteredConversationNotesKey(variables.conversationId);
      onSuccess();
      queryClient.invalidateQueries({
        queryKey: unregisteredConversationNotesKey,
      });
    },
  });

  return {
    updateUnregisteredConversationNote: mutate,
    updateUnregisteredConversationNoteLoading: isLoading,
    ...other,
  };
};
